import { useEffect, useRef } from 'react';
import { useReducedMotion } from '@/core/theme/use-reduced-motion';

const W = 1989;
const H = 668.85;
const LINES = 34;
const STEP = 24;

/** One line of the bundle at time `t` (seconds). The "knot" where all lines meet slides sideways, and the bundle slowly breathes. */
function linePath(i: number, t: number): string {
  const k = (i / (LINES - 1)) * 2 - 1; // -1 … 1 across the bundle
  const amp = 250 * (0.88 + 0.12 * Math.sin(t * 0.7));
  const shift = t * 55;
  let d = '';
  for (let x = 0; x <= W; x += STEP) {
    const swing = Math.sin(((x - 560 - shift) / 1500) * Math.PI * 2);
    const y = H / 2 + k * amp * swing + k * k * 28 * Math.sin((x / W) * Math.PI * 3 + t * 0.5);
    d += `${x === 0 ? 'M' : 'L'}${x} ${y.toFixed(1)} `;
  }
  return d.trim();
}

/** First paint (also server render) is the t = 0 pose, so there is no jump when the animation starts. */
const INITIAL: string[] = Array.from({ length: LINES }, (_, i) => linePath(i, 0));

/**
 * Decorative line art for the hero background (soft yellow, 30% opacity), always in motion:
 * the lines flow and twist continuously. Runs at ~30 fps, pauses while off-screen or in a hidden tab,
 * and stays still for people who asked their device for reduced motion.
 */
export function HeroWaves({ className, fixed = false }: { className?: string; fixed?: boolean }) {
  const svgRef = useRef<SVGSVGElement>(null);
  const reduced = useReducedMotion();
  // `fixed` = a static pose (no animation) that stretches over the whole width of its section.
  const still = reduced || fixed;

  useEffect(() => {
    const svg = svgRef.current;
    if (still || !svg || typeof requestAnimationFrame !== 'function') return;
    const paths = Array.from(svg.querySelectorAll('path'));
    let raf = 0;
    let last = 0;
    let visible = true;
    const t0 = performance.now();

    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      if (!visible || now - last < 33) return;
      last = now;
      const t = (now - t0) / 1000;
      paths.forEach((p, i) => p.setAttribute('d', linePath(i, t)));
    };

    const io =
      typeof IntersectionObserver === 'function'
        ? new IntersectionObserver((entries) => {
            if (entries[0]) visible = entries[0].isIntersecting;
          })
        : null;
    io?.observe(svg);
    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      io?.disconnect();
    };
  }, [still]);

  return (
    <svg
      ref={svgRef}
      {...(fixed ? {} : { width: W, height: H })}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio={fixed ? 'xMidYMid slice' : undefined}
      className={fixed ? `${className ?? ''} inset-0 h-full w-full` : className}
      style={fixed ? undefined : { left: `calc(50% - ${W / 2}px)`, top: `calc(50% - ${H / 2}px)` }}
      aria-hidden
      focusable="false"
    >
      <g fill="none" stroke="#FFE492" strokeWidth="1.1" opacity="0.3">
        {INITIAL.map((d, i) => (
          <path key={i} d={d} />
        ))}
      </g>
    </svg>
  );
}
